import { useState } from "react";
import I18n from "common/i18n";
import { PublishedFilter, MineFilter, PieceSort, PieceSortOrder } from "features/builder/pieceList";
import { MapsSortMenu } from "./MapsSortMenu";
import { MenuOptionRow } from "./MenuOptionRow";
import { SidebarSection } from "./SidebarSection";
import { SidebarDropdown } from "./SidebarDropdown";
import { MenuActionFooter } from "./MenuActionFooter";
import { useBuilderSidebar, SidebarToggleButton, SidebarHeaderBand } from "./BuilderSidebar";

const ACCENT_TEXT = "text-green-100";
const ACCENT_BG = "bg-green-100";
const ACCENT_BORDER = "border-green-100";

const PUBLISHED_ITEMS: { value: PublishedFilter; labelKey: string; icon: string }[] = [
  { value: PublishedFilter.ALL, labelKey: "MSG_BUILDER_FILTER_ALL", icon: "filter_none" },
  {
    value: PublishedFilter.PUBLISHED,
    labelKey: "MSG_BUILDER_CARD_STATUS_PUBLISHED",
    icon: "public",
  },
  {
    value: PublishedFilter.UNPUBLISHED,
    labelKey: "MSG_BUILDER_CARD_STATUS_UNPUBLISHED",
    icon: "lock",
  },
];

const SORT_OPTIONS: PieceSort[] = [
  PieceSort.UPDATED,
  PieceSort.LIKED,
  PieceSort.PLAYED,
  PieceSort.VIEWED,
  PieceSort.REACTION,
];

// こうかいボタンの表示ラベル・アイコン（現在の選択を反映）
const publishedLabel = (p: PublishedFilter): string =>
  p === PublishedFilter.PUBLISHED
    ? "MSG_BUILDER_CARD_STATUS_PUBLISHED"
    : p === PublishedFilter.UNPUBLISHED
      ? "MSG_BUILDER_CARD_STATUS_UNPUBLISHED"
      : "MSG_BUILDER_FILTER_AXIS_PUBLISHED";

const publishedIcon = (p: PublishedFilter): string =>
  p === PublishedFilter.UNPUBLISHED ? "lock" : "public";

// そのほか（つくりかけ/編集中/おきにいり）の有効数
const otherCount = (f: MineFilter): number =>
  (f.incomplete ? 1 : 0) + (f.editing ? 1 : 0) + (f.favorite ? 1 : 0);

type OtherDraft = { incomplete: boolean; editing: boolean; favorite: boolean };

// こうかい吹き出しの中身（下書き＋確定/キャンセル）
const PublishedPanel = ({
  published,
  onApply,
  onClose,
}: {
  published: PublishedFilter;
  onApply: (next: PublishedFilter) => void;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState(published);
  return (
    <div className="flex-col-el items-start w-full gap-3">
      <div className="flex-col-el w-full gap-1">
        {PUBLISHED_ITEMS.map((item) => (
          <MenuOptionRow
            key={item.value}
            icon={item.icon}
            labelKey={item.labelKey}
            active={draft === item.value}
            activeBgClass={ACCENT_BG}
            onClick={() => setDraft(item.value)}
          />
        ))}
      </div>
      <MenuActionFooter
        canApply={draft !== published}
        accentBgClass={ACCENT_BG}
        onApply={() => {
          onApply(draft);
          onClose();
        }}
        secondaryLabel="MSG_STOP_BTN"
        onSecondary={onClose}
      />
    </div>
  );
};

// そのほか吹き出しの中身（下書き＋確定/キャンセル）
const OtherPanel = ({
  value,
  onApply,
  onClose,
}: {
  value: OtherDraft;
  onApply: (next: OtherDraft) => void;
  onClose: () => void;
}) => {
  const [draft, setDraft] = useState<OtherDraft>(value);
  const canApply =
    draft.incomplete !== value.incomplete ||
    draft.editing !== value.editing ||
    draft.favorite !== value.favorite;
  return (
    <div className="flex-col-el items-start w-full gap-3">
      <div className="flex-col-el w-full gap-1">
        <MenuOptionRow
          icon="pending"
          labelKey="MSG_BUILDER_CARD_STATUS_INCOMPLETE"
          active={draft.incomplete}
          activeBgClass={ACCENT_BG}
          onClick={() => setDraft((s) => ({ ...s, incomplete: !s.incomplete }))}
        />
        <MenuOptionRow
          icon="edit"
          labelKey="MSG_BUILDER_CARD_STATUS_EDITING"
          active={draft.editing}
          activeBgClass={ACCENT_BG}
          onClick={() => setDraft((s) => ({ ...s, editing: !s.editing }))}
        />
        <MenuOptionRow
          icon="favorite"
          labelKey="MSG_BUILDER_FILTER_FAVORITE"
          active={draft.favorite}
          activeBgClass={ACCENT_BG}
          onClick={() => setDraft((s) => ({ ...s, favorite: !s.favorite }))}
        />
      </div>
      <MenuActionFooter
        canApply={canApply}
        accentBgClass={ACCENT_BG}
        onApply={() => {
          onApply(draft);
          onClose();
        }}
        secondaryLabel="MSG_BUILDER_CLEAR_BTN"
        onSecondary={() => setDraft({ incomplete: false, editing: false, favorite: false })}
      />
    </div>
  );
};

export const MyMapsHeader = ({
  count,
  sortAxis,
  sortOrder,
  filter,
  onChangeSort,
  onChangeFilter,
}: {
  count: number;
  sortAxis: PieceSort;
  sortOrder: PieceSortOrder;
  filter: MineFilter;
  onChangeSort: (axis: PieceSort, order: PieceSortOrder) => void;
  onChangeFilter: (next: MineFilter) => void;
}) => {
  const { collapsed, toggle } = useBuilderSidebar();
  return (
    <>
      {collapsed ? (
        <div
          onClick={toggle}
          className="flex-col-el items-center w-full gap-1 py-2 border-0 border-b-2 border-solid border-beige-60 cursor-pointer"
        >
          <SidebarToggleButton />
          <span className="flex-col-center w-10 h-10">
            <span className={`material-symbols-outlined type-md ${ACCENT_TEXT}`}>extension</span>
          </span>
        </div>
      ) : (
        <SidebarHeaderBand>
          <span className={`material-symbols-outlined type-md ${ACCENT_TEXT}`}>extension</span>
          <p className="type-text type-body text-textcolor-black whitespace-nowrap">
            {I18n.t("MSG_BUILDER_MAP_COUNT", { count })}
          </p>
        </SidebarHeaderBand>
      )}

      <SidebarSection
        icon="filter_alt"
        labelKey="MSG_BUILDER_SECTION_FILTER"
        accentClass={ACCENT_TEXT}
      >
        <div className="flex-col-el items-start w-full gap-2">
          {/* こうかい（単一選択） */}
          <SidebarDropdown
            icon={publishedIcon(filter.published)}
            label={publishedLabel(filter.published)}
            modalTitle="MSG_BUILDER_FILTER_AXIS_PUBLISHED"
            modalIcon="public"
            accentTextClass={ACCENT_TEXT}
            accentBgClass={ACCENT_BG}
            accentBorderClass={ACCENT_BORDER}
            active={filter.published !== PublishedFilter.ALL}
            wide
          >
            {(close) => (
              <PublishedPanel
                published={filter.published}
                onApply={(p) => onChangeFilter({ ...filter, published: p })}
                onClose={close}
              />
            )}
          </SidebarDropdown>

          {/* そのほか（多選択トグル） */}
          <SidebarDropdown
            icon="tune"
            label="MSG_BUILDER_FILTER_AXIS_OTHER"
            modalTitle="MSG_BUILDER_FILTER_AXIS_OTHER"
            modalIcon="tune"
            showClose
            accentTextClass={ACCENT_TEXT}
            accentBgClass={ACCENT_BG}
            accentBorderClass={ACCENT_BORDER}
            active={otherCount(filter) > 0}
            badge={otherCount(filter)}
            wide
          >
            {(close) => (
              <OtherPanel
                value={{
                  incomplete: filter.incomplete,
                  editing: filter.editing,
                  favorite: filter.favorite,
                }}
                onApply={(v) => onChangeFilter({ ...filter, ...v })}
                onClose={close}
              />
            )}
          </SidebarDropdown>
        </div>
      </SidebarSection>

      <SidebarSection
        icon="swap_vert"
        labelKey="MSG_BUILDER_SECTION_SORT"
        accentClass={ACCENT_TEXT}
      >
        <MapsSortMenu
          axis={sortAxis}
          order={sortOrder}
          options={SORT_OPTIONS}
          accentTextClass={ACCENT_TEXT}
          accentBgClass={ACCENT_BG}
          accentBorderClass={ACCENT_BORDER}
          onChange={onChangeSort}
        />
      </SidebarSection>
    </>
  );
};
